import { openDb, deleteDb, UpgradeDB, DB } from "idb";
import { TTable } from "./TTable";

export class IDB<T> {
	private readonly IDB_NAME: string = "SENDUM_WIDGETS";
	public readonly IDB_VERSION = 31;
	private readonly upgradeCallbackHandler = (db: UpgradeDB): void => {
		if (db.objectStoreNames.contains("history")) {
			db.deleteObjectStore("history");
		}
		if (db.objectStoreNames.contains("sensor")) {
			db.deleteObjectStore("sensor");
		}

		db.createObjectStore("history", {
			keyPath: "id",
			autoIncrement: false,
		});

		db.createObjectStore("sensor", {
			keyPath: "id",
			autoIncrement: false,
		});
	};

	private async openIDB(): Promise<DB> {
		try {
			return await openDb(this.IDB_NAME, this.IDB_VERSION, this.upgradeCallbackHandler);
		} catch (error) {
			if (this.isDownVersion(error)) {
				await this.clean();
				window.location.reload();
			}
			throw error;
		}
	}

	public async init(): Promise<boolean> {
		try {
			const iDB = await this.openIDB();
			iDB.close();

			return true;
		} catch (error) {
			if (this.isDownVersion(error)) {
				await this.clean();
				window.location.reload();
			}

			return false;
		}
	}

	private isDownVersion(error: Error): boolean {
		const versionsCount = 2;
		const currentVersionIndex = 0;
		const existingVersionIndex = 1;
		const versions = error.message?.match(/\d+/g)?.map(Number);

		return versions?.length === versionsCount && versions[currentVersionIndex] < versions[existingVersionIndex];
	}

	public async clean(): Promise<void> {
		await deleteDb(this.IDB_NAME);
	}

	public async clearObjectStore(tableName: string): Promise<void> {
		const idb = await this.openIDB();
		const tx = idb.transaction(tableName, "readwrite");
		const store = tx.objectStore(tableName);
		await store.clear();
		idb.close();
	}

	public async getValue(tableName: TTable, key: string): Promise<T> {
		const idb = await this.openIDB();
		const tx = idb.transaction(tableName, "readonly");
		const store = tx.objectStore(tableName);
		const result = await store.get(key) as T;
		idb.close();

		return result;
	}

	public async getAll(tableName: TTable, query?: IDBKeyRange | IDBValidKey): Promise<T[]> {
		const idb = await this.openIDB();
		const tx = idb.transaction(tableName, "readonly");
		const store = tx.objectStore(tableName);
		const result = await store.getAll(query) as T[];
		idb.close();

		return result;
	}

	public async getKeys(tableName: TTable, query?: IDBKeyRange): Promise<IDBValidKey[]> {
		const idb = await this.openIDB();
		const tx = idb.transaction(tableName, "readonly");
		const store = tx.objectStore(tableName);
		const result = await store.getAllKeys(query) as IDBValidKey[];
		idb.close();

		return result;
	}

	public async putValue(tableName: TTable, value: T): Promise<void> {
		const idb = await this.openIDB();
		const tx = idb.transaction(tableName, "readwrite");
		const store = tx.objectStore(tableName);
		await store.put(value);
		idb.close();
	}

	public async putBulkValue(tableName: TTable, values: T[]): Promise<void> {
		const idb = await this.openIDB();
		const tx = idb.transaction(tableName, "readwrite");
		const store = tx.objectStore(tableName);
		for (const value of values) {
			await store.put(value);
		}
		idb.close();
	}

	public async deleteValue(tableName: TTable, key: IDBKeyRange | IDBValidKey): Promise<boolean> {
		const idb = await this.openIDB();
		const tx = idb.transaction(tableName, "readwrite");
		const store = tx.objectStore(tableName);
		const result = await store.get(key) as T;
		if (!result) {
			return true;
		}
		await store.delete(key);

		return true;
	}
}
